import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getBulkDeleteErrorMessage } from './bulk-delete-error';
import { AritoIcon } from '@/components/custom/arito/icon';
import { LoadingSpinner } from '@/components/custom/erp';
import { useAuth } from '@/contexts/auth-context';
import { toast } from '@/hooks';

interface ActionBarProps {
  handlePin?: () => void;
  handleDeleteRow: () => void;
}

type BulkDeleteActionType = 'delete' | 'pin';

interface ActionButtonConfig {
  type: BulkDeleteActionType;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  icon?: number;
}

const actionButtonConfigs: Record<BulkDeleteActionType, { icon: number; title: string; label: string }> = {
  delete: {
    icon: 8,
    title: 'Xóa dòng đã chọn',
    label: 'Xóa'
  },
  pin: {
    icon: 16,
    title: 'Cố định cột',
    label: 'Cố định cột'
  }
};

const renderActionButton = (button: ActionButtonConfig, loading: boolean) => {
  const fallbackConfig = actionButtonConfigs[button.type];
  const resolvedIcon = button.icon ?? fallbackConfig.icon;
  const resolvedTitle = button.title ?? fallbackConfig.title;
  const resolvedLabel = fallbackConfig.label;
  const isDisabled = button.disabled || loading || false;

  return (
    <TooltipProvider key={button.type}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <button
              className='flex items-center gap-1 rounded-md text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50'
              onClick={button.onClick}
              disabled={isDisabled}
              type='button'
            >
              <AritoIcon icon={resolvedIcon} /> <span>{resolvedLabel}</span>
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{resolvedTitle}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export function ErpDataTableBulkDeleteActionBar({ handlePin, handleDeleteRow }: ActionBarProps) {
  const { loading } = useAuth();
  const buttonActions: Partial<Record<BulkDeleteActionType, (() => void) | undefined>> = {
    pin: handlePin,
    delete: handleDeleteRow
  };

  const buttons: ActionButtonConfig[] = (['delete', 'pin'] as BulkDeleteActionType[])
    .filter(type => buttonActions[type] !== undefined)
    .map(type => ({
      type,
      onClick: buttonActions[type] as () => void,
      disabled: false
    }));

  return (
    <div className='flex h-9 flex-none items-center justify-between border-b border-gray-200 bg-white px-1 py-0'>
      <div className='flex h-full flex-1 items-center justify-between'>
        <div className='flex items-center gap-3'>
          {buttons.map(button => renderActionButton(button, loading))}
          {loading && <LoadingSpinner size={20} />}
        </div>
      </div>
    </div>
  );
}

export const notifyBulkDeleteError = (error: unknown) => {
  console.error('ERP data table bulk delete error:', error);

  toast({
    description: getBulkDeleteErrorMessage(error),
    variant: 'destructive'
  });
};

export const buildBulkDeleteTables = (
  rows: any[],
  columns: any[],
  selectedRowIds: string[],
  setSelectedRowIds: (ids: string[]) => void
) => [
  {
    name: '',
    rows,
    columns,
    checkboxSelection: true,
    selectionModel: selectedRowIds,
    onSelectionModelChange: (newSelection: any) => setSelectedRowIds((newSelection || []).map((id: any) => String(id)))
  }
];

export const TanstackBulkDeleteActionBar = ErpDataTableBulkDeleteActionBar;
